import {
  COURTS,
  type Court,
  type MoveRow as ListRow,
  type PlayerNumber,
  type PlayerRecord,
  type SessionState,
} from '@/domain/session';

// The tick, as a pure function. Pinned by session/contracts/p4-rotation.md (Surface A) and by the
// blind oracle beside this file, which the implementation may not edit.
//
// Nothing here reads a clock or a random source, and nothing here writes an event. One state in,
// one list out, and the same state must always produce the same list: every device folds the same
// stream, and a tick that reached for a device clock or a random tiebreak would make the admin
// laptop's answer unreproducible anywhere else (docs/rotation-algorithm.md, "Ties").
//
// It is also status-blind. `computeTick` decides WHAT a tick says; the engine decides WHEN one
// runs (Surface B). A gate in here would be a second opinion about the session's state machine.

export interface TickParams {
  // The outlier threshold, in population standard deviations. Admin-tunable during the session
  // through `thresholdSet`, so it arrives as a parameter and is never a constant in here.
  readonly sigma: number;
  // How far the fullest court may exceed the emptiest before a rebalance move fires. Default 2
  // (docs/rotation.md, "Balance").
  readonly tolerance: number;
  // 1..6. Carried so a caller can label the list; the arithmetic does not depend on it. The
  // 60-minute tick's different PLACEMENT is the engine's business, not this function's.
  readonly tickIndex: number;
}

// Why a row is in the list. The mover never sees this — the shipped `moveListComputed` payload
// carries `{ player, from, to }` and nothing else (src/domain/aggregates.ts, typeMajor 2005), so
// the engine strips this field on the way to the event. It exists because the two steps of the
// tick are one calculation and the exclusion rule ("a player in an earned move is never selected
// again in the rebalance") needs the distinction to be checkable.
export type MoveReason = 'earned-up' | 'earned-down' | 'rebalance';

// Exactly the event's row plus the reason, so the strip is a field removal and never a re-shape.
export interface MoveRow extends ListRow {
  readonly reason: MoveReason;
}

// How many rebalance rows one tick may produce. Two rows per court is a list a single mover can
// walk in ten minutes, which is what the number is measured against: there may be only one mover
// for the whole session (docs/access.md), and a list nobody finishes is worse than a partial fix
// that the next tick continues. It bounds step two only.
export const MAX_REBALANCE_ROWS = 8;

// Earned moves for every court first, then net headcount deltas, then rebalance. One list, no
// player in it twice, every row one level (docs/rotation-algorithm.md, "Cascades and exclusions").
//
// Struck-off players are not on a court: they are never selected and never counted in a headcount
// (docs/roster.md). Outliers are STRICT — `score > mean + sigma*sd` up, `score < mean - sigma*sd`
// down, over the population deviation — so a uniform court moves nobody.
export function computeTick(state: SessionState, params: TickParams): readonly MoveRow[] {
  // One pass over the roster, and the ONLY place struck-off players are dropped. Everything below
  // reads this array: a player who went home is not in a distribution and not in a headcount.
  const active = state.players.filter((player) => !player.struckOff);
  const rows: MoveRow[] = [];
  // Every player already in the list. This is the exclusion rule and it is also what bounds the
  // rebalance loop: each iteration must consume a player who is not in here yet.
  const listed = new Set<PlayerNumber>();
  // Headcounts as the list would leave them. Step one moves the counters before step two reads
  // them, which is what makes the counter-move fall out of the arithmetic rather than being a
  // special case (docs/rotation-algorithm.md, "Counter-moves").
  const counts = headcounts(active);

  // --- Step one: earned moves, every court, one pass ---------------------------------------
  for (const court of COURTS) {
    for (const row of earnedOn(active, court, params.sigma)) {
      rows.push(row);
      listed.add(row.player);
      counts[row.from] -= 1;
      counts[row.to] += 1;
    }
  }

  // --- Step two: rebalance ------------------------------------------------------------------
  // While the fullest court exceeds the emptiest by MORE than the tolerance, and a legal move
  // exists, move one player one level toward the emptier end. One level at a time, so a badly
  // imbalanced court cannot be fixed inside one tick and the design accepts the partial fix.
  //
  // Bounded twice. The roster is the correctness bound — every iteration adds a player to `listed`
  // and no player is added twice, so the loop cannot outrun the hall. `MAX_REBALANCE_ROWS` is the
  // product bound, and it is the one that fires: a desk that dealt the courts badly, or a session
  // resumed after a long stop, produces a headcount gap that the one-level rule can only close a
  // player at a time, and the arithmetic will happily hand a single mover a list of forty-nine
  // rows to walk. A partial fix is already the design (docs/rotation-algorithm.md, "Cascades and
  // exclusions"); this decides how partial, and the next tick is ten minutes away.
  //
  // Step one is NOT capped. An earned move is coach signal and every outlier moves, ties included.
  for (let remaining = Math.min(active.length, MAX_REBALANCE_ROWS); remaining > 0; remaining--) {
    const row = rebalanceStep(active, listed, counts, params.tolerance);
    if (row === undefined) break;
    rows.push(row);
    listed.add(row.player);
    counts[row.from] -= 1;
    counts[row.to] += 1;
  }

  return rows;
}

// ---------------------------------------------------------------------------------------------
// Step one
// ---------------------------------------------------------------------------------------------

// Outlier detection on one court's score distribution (docs/rotation-algorithm.md, "Outlier
// detection"). Population deviation, divide by N: the court IS the population, not a sample drawn
// from one, and the sample deviation would widen the band on exactly the small courts this product
// runs.
//
// The mean here is the exact arithmetic mean, NOT the ceiling-rounded `courtMean` the fold uses for
// an arrival's score. Rounding is a rule about what score a person is dealt; the detector is
// measuring a spread, and rounding its centre would bias every band by up to a point.
function earnedOn(
  active: readonly PlayerRecord[],
  court: Court,
  sigma: number,
): readonly MoveRow[] {
  const on = active.filter((player) => player.court === court);
  // An empty court, a single player, or a court where everyone holds the same number has told the
  // tick nothing. sd = 0 is not "everybody is an outlier" and it is not a division by zero.
  if (on.length < 2) return [];
  const mean = on.reduce((sum, player) => sum + player.score, 0) / on.length;
  const variance = on.reduce((sum, player) => sum + (player.score - mean) ** 2, 0) / on.length;
  const sd = Math.sqrt(variance);
  if (sd === 0) return [];

  const up = COURTS[COURTS.indexOf(court) + 1];
  const down = COURTS[COURTS.indexOf(court) - 1];
  const rows: MoveRow[] = [];
  for (const player of on) {
    // STRICT on both sides: a score sitting exactly on the band's edge stays. Two players tied at
    // the same outlier score both move (docs/rotation-algorithm.md, "Ties"), which falls out of
    // testing every player rather than picking a top one.
    if (player.score > mean + sigma * sd) {
      // Gold cannot be promoted. There is nowhere above it, and the answer is no row rather than
      // a row off the end of the ladder.
      if (up !== undefined) rows.push(row(player, court, up, 'earned-up'));
    } else if (player.score < mean - sigma * sd) {
      if (down !== undefined) rows.push(row(player, court, down, 'earned-down'));
    }
  }
  return rows;
}

// ---------------------------------------------------------------------------------------------
// Step two
// ---------------------------------------------------------------------------------------------

// One rebalance move, or undefined when the room is inside tolerance or nothing legal is left.
//
// "Nothing legal is left" is the other half of termination: a court whose simulated headcount is
// the fullest in the hall may hold no player this list has not already named — every one of them
// arrived here on a row computed a moment ago. Stopping there is the partial fix the design
// accepts; picking a different court instead would move people away from the balance the earlier
// rows were paying for.
function rebalanceStep(
  active: readonly PlayerRecord[],
  listed: ReadonlySet<PlayerNumber>,
  counts: Record<Court, number>,
  tolerance: number,
): MoveRow | undefined {
  const fullest = extremeCourt(counts, 'max');
  const emptiest = extremeCourt(counts, 'min');
  // MORE than the tolerance. A spread of exactly two at tolerance two is a room that is balanced
  // enough, and shuffling players to fix an off-by-one is worse than the imbalance
  // (docs/rotation.md, "Balance").
  if (counts[fullest] - counts[emptiest] <= tolerance) return undefined;

  // One level TOWARD the emptier end. The destination is the next court along, never the emptiest
  // court itself: the one-level rule is what stops a single tick teleporting a hall across.
  const step = COURTS.indexOf(emptiest) > COURTS.indexOf(fullest) ? 1 : -1;
  const to = COURTS[COURTS.indexOf(fullest) + step];
  if (to === undefined) return undefined;

  const chosen = boundaryOn(active, listed, fullest, step === 1 ? 'up' : 'down');
  if (chosen === undefined) return undefined;
  return row(chosen, fullest, to, 'rebalance');
}

// The selection rule follows the DIRECTION of travel: a move down takes the lowest score (being at
// the bottom of a court means expecting to get moved), a move up takes the highest (the player
// closest to belonging there). One rule for both directions used to be "lowest", which promoted
// the WORST player of an over-full court into the division above — caught on the first live
// grading-day test, a player on -4 forecast into gold past a courtmate on +12. Ties either way:
// longest on this court, then highest player number (docs/rotation-algorithm.md, "Ties").
//
// "Longest on this court" is `arrivalSeq`, which the fold derives from the event stream's own
// ordering — never from wall time on whichever machine is asking. The third rule is a formal
// backstop: arrival order is a total order over the roster, so two players on one court cannot
// share a sequence number and this branch is unreachable from a real room. It is here anyway,
// because determinism is a system invariant and an unreachable tiebreak costs one comparison.
function boundaryOn(
  active: readonly PlayerRecord[],
  listed: ReadonlySet<PlayerNumber>,
  court: Court,
  direction: 'up' | 'down',
): PlayerRecord | undefined {
  let best: PlayerRecord | undefined;
  for (const player of active) {
    if (player.court !== court || listed.has(player.number)) continue;
    if (best === undefined || beats(player, best, direction)) best = player;
  }
  return best;
}

function beats(candidate: PlayerRecord, best: PlayerRecord, direction: 'up' | 'down'): boolean {
  if (candidate.score !== best.score) {
    return direction === 'down' ? candidate.score < best.score : candidate.score > best.score;
  }
  if (candidate.arrivalSeq !== best.arrivalSeq) return candidate.arrivalSeq < best.arrivalSeq;
  return candidate.number > best.number;
}

// ---------------------------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------------------------

function headcounts(active: readonly PlayerRecord[]): Record<Court, number> {
  const counts: Record<Court, number> = { white: 0, red: 0, purple: 0, gold: 0 };
  for (const player of active) counts[player.court] += 1;
  return counts;
}

// Ties resolve on the canonical court order, so two devices holding identical counts pick the same
// court. `COURTS` is iterated rather than `Object.keys`, which would put determinism at the mercy
// of an object's insertion order.
function extremeCourt(counts: Record<Court, number>, which: 'max' | 'min'): Court {
  let best: Court = COURTS[0];
  for (const court of COURTS) {
    const better = which === 'max' ? counts[court] > counts[best] : counts[court] < counts[best];
    if (better) best = court;
  }
  return best;
}

function row(player: PlayerRecord, from: Court, to: Court, reason: MoveReason): MoveRow {
  return { player: player.number, from, to, reason };
}
